import { put } from 'core/network/RestApi';
import { ClientLevel } from 'core/types';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useParams } from 'react-router';
import { IDbFileExportConfig } from '../entities/fileExportConfig';
import { FileExportConfigPage } from './FileExportConfigPage';
import * as RestApi from 'core/network/RestApi';

export const EditFileExportConfigPage = () => {
  const { clientId, folderName } = useParams<{ clientId: string; folderName: string }>();

  const [s3SubFolder, setS3SubFolder] = useState(folderName);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [timestampRemoval, setTimestampRemoval] = useState(false);
  const [timestampFormat, setTimestampFormat] = useState(false);
  const [compressFile, setCompressFile] = useState(false);
  const [fileExportConfig, setFileExportConfig] = useState(null);

  const location = useLocation<{
    clientLevel: ClientLevel;
    clientName: string;
  }>();

  const { clientLevel, clientName } = location.state || {};

  const putNewFileExportConfig = useMutation((fileExportConfig: IDbFileExportConfig) =>
    put<{ clientId: string }, IDbFileExportConfig>(
      `/${clientLevel}s/${clientId}/file_export/${folderName}`,
      fileExportConfig,
    ),
  );

  useEffect(() => {
    if (!fileExportConfig) {
      return;
    }

    const tx = fileExportConfig.transformations;
    setSaveEnabled(
      !putNewFileExportConfig.isLoading &&
        (tx.timestampFormat.active !== timestampFormat ||
          tx.timestampRemoval.active !== timestampRemoval ||
          tx.compression.active !== compressFile),
    );
  }, [timestampFormat, timestampRemoval, compressFile, fileExportConfig, putNewFileExportConfig.isLoading]);

  const discardChanges = () => {
    setS3SubFolder(folderName);
    setTimestampFormat(fileExportConfig.transformations.timestampFormat.active);
    setTimestampRemoval(fileExportConfig.transformations.timestampRemoval.active);
    setCompressFile(fileExportConfig.transformations.compression.active);
  };

  const getFolderConfigQuery = useQuery(['get-file-export-folder-config', clientId], async () => {
    if (!clientId || !folderName) return null;
    const result = await RestApi.get<IDbFileExportConfig>(`/${clientLevel}s/${clientId}/file_export/${folderName}`);
    return result.data;
  });

  useEffect(() => {
    if (!folderName) {
      return;
    }

    if (!getFolderConfigQuery.isLoading) {
      const config = getFolderConfigQuery.data;
      setFileExportConfig(config);
      setTimestampRemoval(config.transformations.timestampRemoval.active);
      setTimestampFormat(config.transformations.timestampFormat.active);
      setCompressFile(config.transformations.compression.active);
    }
  }, [getFolderConfigQuery.isLoading, getFolderConfigQuery.data, folderName]);

  const showError = getFolderConfigQuery.isError;

  return (
    <FileExportConfigPage
      s3SubFolder={s3SubFolder}
      timestampRemoval={timestampRemoval}
      timestampFormat={timestampFormat}
      compressFile={compressFile}
      saveEnabled={saveEnabled}
      clientId={clientId}
      onDiscardChanges={discardChanges}
      mutate={putNewFileExportConfig.mutate}
      clientName={clientName}
      clientLevel={clientLevel}
      folderName={folderName}
      showError={showError}
      error={getFolderConfigQuery.error as Error}
      setTimestampRemoval={setTimestampRemoval}
      setTimestampFormat={setTimestampFormat}
      setCompressFile={setCompressFile}
      setS3SubFolder={setS3SubFolder}
    />
  );
};
